import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import ThemeSwitcher from './ThemeSwitcher';
import './GlobalNav.css';
import { useAuth } from '../context/AuthContext.jsx';

const DRAFT_STORAGE_KEYS = [
  'plan',
  'milestones',
  'guests',
  'guestSettings',
  'decider',
  'journeySettings',
  'journeyHost',
  'journeyHostProfile',
  'journeyHostUid',
];

const META_KEYS = ['momentsDraftId', 'momentsDraftMeta'];

function clearMomentDraft() {
  if (typeof window === 'undefined') return;
  try {
    DRAFT_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
    META_KEYS.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

export default function GlobalNav() {
  const [gamesOpen, setGamesOpen] = useState(false);
  const [hostMenuOpen, setHostMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAdmin } = useAuth();
  const marketingDropdownRef = useRef(null);
  const hostMenuRef = useRef(null);
  const hostButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const authenticatedUser = user && !user.isAnonymous ? user : null;
  const hostDisplayName =
    authenticatedUser?.displayName || authenticatedUser?.email || 'Host';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (marketingDropdownRef.current && !marketingDropdownRef.current.contains(event.target)) {
        setGamesOpen(false);
      }
      if (
        hostMenuOpen &&
        hostMenuRef.current &&
        !hostMenuRef.current.contains(event.target) &&
        hostButtonRef.current &&
        !hostButtonRef.current.contains(event.target)
      ) {
        setHostMenuOpen(false);
      }
      if (
        mobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [hostMenuOpen, mobileMenuOpen]);

  useEffect(() => {
    if (!authenticatedUser && hostMenuOpen) {
      setHostMenuOpen(false);
    }
  }, [authenticatedUser, hostMenuOpen]);

  const handleGameClick = (path) => {
    setGamesOpen(false);
    navigate(path);
  };

  const handleHostAction = async (action) => {
    setHostMenuOpen(false);
    if (action === 'moments') {
      navigate('/planner/profile');
      return;
    }
    if (action === 'account') {
      navigate('/account');
      return;
    }
    if (action === 'admin') {
      navigate('/admin');
      return;
    }
    if (action === 'signout') {
      clearMomentDraft();
      const auth = getAuth();
      try {
        await auth.signOut();
      } catch (error) {
        console.error('Sign out failed:', error);
      } finally {
        navigate('/planner');
      }
    }
  };

  return (
    <header className="global-nav">
      <div className="global-nav__inner">
        {/* Logo - Always left */}
        <Link to="/" className="global-nav__brand">
          <img
            src="/images/New%20_PartyDecider%20transparent%20logo.png"
            alt="PartyDecider digital RSVP and event-planning logo"
            className="global-nav__brand-logo"
          />
          PartyDecider
        </Link>

        {/* Desktop Menu - Center (hidden on mobile) */}
        <nav className="global-nav__menu global-nav__menu--desktop">
          <div className="global-nav__dropdown" ref={marketingDropdownRef}>
            <button
              className="global-nav__link global-nav__link--dropdown"
              onClick={() => setGamesOpen((open) => !open)}
              aria-expanded={gamesOpen}
              aria-haspopup="true"
            >
              PartyPack
              <span className={`global-nav__arrow ${gamesOpen ? 'open' : ''}`}>▾</span>
            </button>
            {gamesOpen && (
              <div className="global-nav__dropdown-menu">
                <button
                  onClick={() => handleGameClick('/whoispaying')}
                  className="global-nav__dropdown-item"
                >
                  🎲 Who Is Paying
                </button>
                <button
                  onClick={() => handleGameClick('/planner')}
                  className="global-nav__dropdown-item"
                >
                  🗓️ Planner
                </button>
                <button
                  onClick={() => handleGameClick('/raffle')}
                  className="global-nav__dropdown-item global-nav__dropdown-item--disabled"
                  disabled
                >
                  🎟️ Raffle Tickets
                  <span className="global-nav__badge">Soon</span>
                </button>
              </div>
            )}
          </div>

          <Link to="/about" className="global-nav__link">
            About
          </Link>
        </nav>

        {/* Desktop Theme Switcher (hidden on mobile) */}
        <div className="global-nav__theme-desktop">
          <ThemeSwitcher />
        </div>

        {/* Mobile Burger Menu Button (hidden on desktop) */}
        <button
          type="button"
          className="global-nav__burger"
          onClick={() => setMobileMenuOpen((open) => !open)}
          aria-label="Menu"
          aria-expanded={mobileMenuOpen}
        >
          <span className={`global-nav__burger-icon ${mobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        {/* User Chip - Always right (or hidden if not logged in) */}
        {authenticatedUser && (
          <div className="global-nav__user">
            <div className="global-nav__user-chip-wrapper" ref={hostMenuRef}>
              <button
                type="button"
                className="journey-host-chip global-nav__user-chip"
                onClick={() => setHostMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={hostMenuOpen}
                ref={hostButtonRef}
              >
                {authenticatedUser.photoURL ? (
                  <img
                    src={authenticatedUser.photoURL}
                    alt="PartyDecider host profile avatar"
                    className="journey-host-chip__avatar"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="journey-host-chip__avatar journey-host-chip__avatar--fallback" aria-hidden="true">
                    {(hostDisplayName || 'H')[0]?.toUpperCase?.() || 'H'}
                  </div>
                )}
                <div className="journey-host-chip__info">
                  <span className="journey-host-chip__name">{hostDisplayName}</span>
                </div>
                <span className="journey-host-chip__chevron" aria-hidden="true">
                  ▾
                </span>
              </button>
              {hostMenuOpen && (
                <div className="journey-host-menu" role="menu">
                  <button type="button" role="menuitem" onClick={() => handleHostAction('moments')}>
                    My Plans
                  </button>
                  <button type="button" role="menuitem" onClick={() => handleHostAction('account')}>
                    Account
                  </button>
                  {isAdmin && (
                    <button type="button" role="menuitem" onClick={() => handleHostAction('admin')}>
                      Admin Center
                    </button>
                  )}
                  <button type="button" role="menuitem" onClick={() => handleHostAction('signout')}>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="global-nav__mobile-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div
            className="global-nav__mobile-menu"
            onClick={(e) => e.stopPropagation()}
            ref={mobileMenuRef}
          >
            <nav className="global-nav__mobile-nav">
              <button
                onClick={() => {
                  handleGameClick('/whoispaying');
                  setMobileMenuOpen(false);
                }}
                className="global-nav__mobile-item"
              >
                🎲 Who Is Paying
              </button>
              <button
                onClick={() => {
                  handleGameClick('/planner');
                  setMobileMenuOpen(false);
                }}
                className="global-nav__mobile-item"
              >
                🗓️ Planner
              </button>
              <button
                onClick={() => handleGameClick('/raffle')}
                className="global-nav__mobile-item global-nav__mobile-item--disabled"
                disabled
              >
                🎟️ Raffle Tickets
                <span className="global-nav__badge">Soon</span>
              </button>
              
              <div className="global-nav__mobile-divider"></div>
              
              <button
                onClick={() => {
                  navigate('/about');
                  setMobileMenuOpen(false);
                }}
                className="global-nav__mobile-item"
              >
                About
              </button>


              <div className="global-nav__mobile-divider"></div>

              <div className="global-nav__mobile-theme">
                <ThemeSwitcher />
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
